<template>
  <div v-if="demandeIntervention">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ $t('detailIntervention.title', { code: demandeIntervention.code }) }}
              </h1>
              <breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <dropdownButton
              type="is-addOrEdit"
              :permission="constantes.INTERVENTION.maj"
              :actions="actionSecondary"
              @click="() => {
                $router.push({ name: 'm.logistique.demande_intervention-edition',
                  params: { id: demandeIntervention.id}});
                }">
                {{ $t('general.button.edit') }}
            </dropdownButton>
          </div>
        </div>
      </div>
    </div>
    <onglet-manager
      v-if="demandeIntervention"
      class="mgt-20px"
      :onglets="onglets">
      <template #informations>
        <informations :demandeIntervention="demandeIntervention" />
      </template>
      <template #faces>
        <tab-select-faces-demande-logistique
          :listeFacesBase="listeFacesBase"
          :displayColumnLogistique="true"
          :isMairie="isMairie"
          :isDetails="true"
        />
      </template>
    </onglet-manager>
    <b-modal v-model="popUpPrevention" has-modal-card class="modal">
      <pop-up-prevention :mess-header="infoPopUpPrevention.title"
                         :mess-description="infoPopUpPrevention.sousTitle"
                         :messagesPopup="messagesErreurPopUp"
                         :isErrorPopup.sync="errorPopUp"
                         :previousButton="$t('general.button.cancel')"
                         :cancel="$t('general.non')"
                         :validate="$t('general.oui')"
                         @previousButton="popUpPrevention = false"
                         @yes="updateEtatDemande(true)"
                         @no="updateEtatDemande(false)">
      </pop-up-prevention>
    </b-modal>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import breadcrumb from '@/components/general/breadcrumb';
import { INTERVENTION } from '@/configuration/logistique/permissionsLogistiqueConf';
import dropdownButton from '@/components/general/button/dropdownButton';
import { get, updateEtatAsync } from '@/api/logistique/demandeLogistique/index';
import { fillInfoOtPatrimoine } from '@/services/logistique/ordreTravail/tech/fillInfoOtPatrimoine';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import {
  ETAT_VALIDE,
  ETAT_TRAITE,
  ETAT_REFUSE,
  INTERVENTION_TYPE_INTERVENTION,
} from '@/configuration/logistique/intervention/interventionConf';
import snackBarSuccess from '@/services/general/snackbar/snackBarSuccess';
import messageSucces from '@/services/general/messageSucces';
import { ETAT_REALISE } from '@/configuration/logistique/ordreTravail/ordreTravailConf';
import popUpPrevention from '@/components/general/popUpPrevention';
import validation from '@/services/logistique/intervention/validationIntervention';
import Informations from '@/components/logistique/demande/details/demandeInformations';
import { getLignesSelectionFaceForDemandeAffichage } from '@/services/dcf/reseauTempo/getListeFacesForSelection';
import { CATEGORIE_REGROUPEMENT, CATEGORIE_REGROUPEMENT_LC, CATEGORIE_REGROUPEMENT_MAIRIE } from '@/configuration/catalogue/produitConf';
import ongletManager from '@/components/general/ongletManager';
import tabSelectFacesDemandeLogistique
  from '@/components/devisCommandeFacture/facesSelectInReseauTempo/tabSelectFacesDemandeLogistique';

const Attachments = createNamespacedHelpers('attachments');
const Permissions = createNamespacedHelpers('permissions');
const Parametres = createNamespacedHelpers('parametres');
const Societe = createNamespacedHelpers('societe');

export default {
  data() {
    return {
      demandeIntervention: null,
      constantes: {
        INTERVENTION,
        ETAT_REFUSE,
        ETAT_TRAITE,
        ETAT_VALIDE,
      },
      infoPopUpPrevention: {
        title: '',
        sousTitle: '',
      },
      etatSelect: null,
      popUpPrevention: false,
      messagesErreurPopUp: [],
      errorPopUp: false,
      listeFacesBase: [],
    };
  },
  components: {
    dropdownButton,
    breadcrumb,
    popUpPrevention,
    Informations,
    tabSelectFacesDemandeLogistique,
    ongletManager,
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeIntervention.breadcrumb.listeIntervention'), route: { name: 'l.logistique.demande_intervention' }, isActive: false },
        { title: this.$t('detailIntervention.breadcrumb.detail'), route: '', isActive: true },
      ];
    },
    onglets() {
      const tabs = [
        {
          key: 'informations',
          title: this.$t('demandeLogistique.creationOrUpdate.tab.informations'),
        },
      ];
      if (this.isProduitRegroupement) {
        tabs.push({
          key: 'faces',
          title: this.$t('demandeLogistique.creationOrUpdate.tab.faces', { nbrFacesSelected: this.nbrFacesSelected }),
        });
      }
      return tabs;
    },
    nbrFacesSelected() {
      const number = this.listeFacesBase.filter(f => f.selected).length;
      if (number > 0) return `(${number})`;
      return '';
    },
    actionSecondary() {
      const actions = [];

      if (this.demandeIntervention.etat !== ETAT_VALIDE) {
        actions.push(
          {
            label: this.$t('detailIntervention.actions.valider'),
            action: () => { this.updateEtatToValide(); },
            permissionAction: INTERVENTION.maj,
          },
        );
      }
      if (this.demandeIntervention.etat !== ETAT_TRAITE) {
        actions.push(
          {
            label: this.$t('detailIntervention.actions.traiter'),
            action: () => { this.updateEtatTraite(); },
            permissionAction: INTERVENTION.maj,
          },
        );
      }
      if (this.demandeIntervention.etat !== ETAT_REFUSE
      && !this.demandeIntervention.otTechnique) {
        actions.push(
          {
            label: this.$t('detailIntervention.actions.refuser'),
            action: () => { this.updateEtatDemande(false, ETAT_REFUSE); },
            permissionAction: INTERVENTION.maj,
          },
        );
      }

      return actions;
    },
    isProduitRegroupement() {
      const regroupements = [
        CATEGORIE_REGROUPEMENT,
        CATEGORIE_REGROUPEMENT_MAIRIE,
        CATEGORIE_REGROUPEMENT_LC,
      ];
      return regroupements
        .some(r => r === this.demandeIntervention.typeProduit);
    },
    isMairie() {
      return this.demandeIntervention.typeProduit === CATEGORIE_REGROUPEMENT_MAIRIE;
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
      loadParametres: 'loadParametres',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Attachments.mapActions({
      showAddAttachmentModal: 'showAddModal',
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
    }),
    async updateEtatToValide() {
      this.messagesErreurPopUp = [];
      this.errorPopUp = false;
      if (!this.demandeIntervention.otTechnique
        && this.demandeIntervention.type === INTERVENTION_TYPE_INTERVENTION) {
        this.infoPopUpPrevention = {
          title: this.$i18n.t('detailIntervention.etat.valide.title'),
          sousTitle: this.$i18n.t('detailIntervention.etat.valide.sousTitle'),
        };
        this.popUpPrevention = true;
        this.etatSelect = ETAT_VALIDE;
        return;
      }
      await this.updateEtatDemande(false, ETAT_VALIDE);
    },
    async updateEtatTraite() {
      this.messagesErreurPopUp = [];
      this.errorPopUp = false;
      if (this.demandeIntervention.otTechnique
        && this.demandeIntervention.otTechnique.etat !== ETAT_REALISE) {
        this.infoPopUpPrevention = {
          title: this.$i18n.t('detailIntervention.etat.traite.title'),
          sousTitle: this.$i18n.t('detailIntervention.etat.traite.sousTitle'),
        };
        this.popUpPrevention = true;
        this.etatSelect = ETAT_TRAITE;
        return;
      }
      await this.updateEtatDemande(false, ETAT_TRAITE);
    },
    async updateEtatDemande(action, etat) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        this.messagesErreurPopUp = [];
        this.errorPopUp = false;
        const data = {
          demandeIds: [this.demandeIntervention.id],
          etat: etat || this.etatSelect,
          actionUpdateEtat: action,
        };
        await updateEtatAsync(data);
        this.popUpPrevention = false;
        if (this.etatSelect === ETAT_VALIDE && action) {
          await this.$router.push({
            name: 'c.logistique.demande_intervention-displaySnackBar',
            params: {
              id: this.demandeIntervention.id,
              displaySnackBar: true,
            },
          });
          return loadingComponent.close();
        } if (this.etatSelect === ETAT_TRAITE && action) {
          messageSucces(this.$i18n.t('detailIntervention.etat.valide.successCreation'));
        } else messageSucces(this.$t('detailIntervention.etat.refuser.success'));
        if (this.$route.name === 'c.logistique.demande_intervention-displaySnackBar') {
          await this.$router.push({
            name: 'c.logistique.demande_intervention',
            params: { id: this.demandeIntervention.id },
          });
        } else this.$router.go(0);
        return loadingComponent.close();
      } catch (e) {
        if (e.response.data.error) {
          validation(e.response, this.messagesErreurPopUp);
          this.errorPopUp = true;
        } else messageErreur(this.$i18n);
        loadingComponent.close();
        throw e;
      }
    },
    async initSelFaces() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const users = this.parametre(PARAMETRES_KEYS.utilisateur);
        const liste = await getLignesSelectionFaceForDemandeAffichage(
          this.demandeIntervention,
          users, this.societeActiveId,
          this.demandeIntervention.datePlanification);
        liste.sort((a, b) => a.affecteLogistiquement - b.affecteLogistiquement);
        this.listeFacesBase = cloneDeep(liste)
          .map(face => {
            const dmdLogistiqueSelProduit = this
              .demandeIntervention?.demandeLogistiqueSelProduit
              .find(f => f.produitId === face.produitId);
            if (dmdLogistiqueSelProduit) {
              face.selected = true;
            }
            return face;
          });
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametreUtilisateur();
      await this.loadParametres([PARAMETRES_KEYS.typeOt]);
      await this.setAttachmentContext({ module: 'DemandeLogistique' });
      await this.setAttachmentElementId({ id: this.$route.params.id });
      const demandeIntervention = (await get(this.$route.params.id)).data.result;
      demandeIntervention.infoPatrimoine = {
        faceId: demandeIntervention.faceId,
        materielId: demandeIntervention.materielId,
        dispositifId: demandeIntervention.dispositifId,
        emplacementId: demandeIntervention.emplacementId,
      };
      await fillInfoOtPatrimoine(demandeIntervention.infoPatrimoine, true);
      if (this.$route.params.displaySnackBar === true) {
        snackBarSuccess(
          this.$i18n.t('detailIntervention.etat.valide.successCreation'),
          (demandeIntervention.otTechnique
            ? this.$i18n.t('detailIntervention.etat.valide.goToOt') : null),
          (demandeIntervention.otTechnique
            ? () => {
              this.$router.push({
                name: 'c.logistique.ordre_travail-tech',
                params: { id: demandeIntervention.otTechnique.id },
              });
            } : null),
        );
      }
      this.demandeIntervention = demandeIntervention;
      if (this.isProduitRegroupement) {
        await this.initSelFaces();
      }
      this.setInformationPage(this.$t('pageGeneral.detail', { item: this.demandeIntervention.code }));
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
};

</script>
